import BackgroundImage from '@assets/svgs/logo-marketeyeq-wht.svg';
import { useCommon } from '@hooks/common/UseCommon';
import { useSearchDeals } from '@hooks/header/UseSearchDeals';
import { useSideNav } from '@hooks/side-nav/UseSideNav';
import { useCheckDevice } from '@hooks/UseCheckDevice';
import { setLeftMobileMenu } from '@store/portfolio/PortfolioActions';
import { PortfolioState } from '@store/portfolio/PortfolioReducer';
import { setIsCustomerProfileModalOpen } from '@store/service-portfolio/ServiceActions';
import { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CurrentUser } from '@appTypes/CurrentUser';
import { DataExportCount } from '@appTypes/DataExport';
import { DealerState } from '@appTypes/DealerState';
import { StoreDetails } from '@appTypes/StoreDetails';
import { NavType } from '@enums/NavType';
import { usePwa } from '@hooks/UsePwa';
import { useUserDealer } from '@hooks/UseUserDealer';
import { logoutRedirect } from '@services/auth/AuthService';
import { CommonService } from '@services/common/CommonService';
import { currentYear } from '@services/utils';
import { AppState } from '@store/AppState';
import { theme } from '@styles/theme';
import styled from 'styled-components';
import SelectStoreModal from '../../../header/user-menu-header/SelectStoreModal';
import { IF } from '../../../shared/generics';
import { Icon } from '../../../style';
import {
  BottomMenu,
  ChangeButton,
  Footer,
  Header,
  HeaderArea,
  HeaderImage,
  IconWrapper,
  MainMenu,
  MainMenuColumnWrapper,
  MainMenuItemList,
  MenuItem,
  NavBottomText,
  NavLink,
  SideNavDiv,
  StoreName,
} from './MobileSideNavStyles';

export type MobileSideNavProps = {
  currentUrl: string;
};

const MobileSideNav: FC<MobileSideNavProps> = ({ currentUrl }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const privateIncentiveCount = useSelector<AppState, DataExportCount>(
    (state) => state.dealer.privateIncentiveCount,
  );
  const { leftMobileMenu } = useSelector<AppState, PortfolioState>((state) => state.portfolio);
  const { dealerList } = useCommon();
  const { dealerInfos, featureToggle, storeServices } = useSelector<AppState, DealerState>(
    (state) => state.dealer,
  );
  const { buildLoyaltyUrl, redirectToUrl, isActiveTab } = useSideNav(currentUrl);
  const currentUser = useSelector<AppState, CurrentUser>((state) => state.currentUser);
  const [selectStoreModalIsOpen, setSelectStoreModalIsOpen] = useState<boolean>(false);
  const [storeName, setStoreName] = useState<string>('');
  const [showChangeButton, setShowChangeButton] = useState<boolean>(true);
  const { isIncentiveAdvantageType, isEssentialType } = useUserDealer();
  const currentHomePage = localStorage.getItem('homePageOptionUrl');
  const isPrivateIncentiveLookup = dealerInfos.some((e) => dealerList.includes(e.brandName));
  const isServicePortfolio = featureToggle.servicePortfolio;
  const hasServicePortfolioEnabled = storeServices && storeServices.hasServicePortfolio;
  const mobileEnabled = featureToggle.mobileEnabled;
  const servicePortfolioNav =
    isServicePortfolio && hasServicePortfolioEnabled ? NavType.Service : NavType.ServiceToSales;
  const { isDealerOptIn } = useSearchDeals();
  const [selectedOption, setSelectedOption] = useState(null);
  const { isPwa } = usePwa();
  const { isLandScape: isLandscape } = useCheckDevice();

  const dispatch = useDispatch();

  const hideSideNav = (): void => {
    document.body.style.overflow = 'auto';
    dispatch(setLeftMobileMenu(false));
  };

  useEffect(() => {
    function setSelectedNavOption() {
      if (currentUrl.includes(NavType.Portfolio.toLowerCase())) {
        setSelectedOption(NavType.Loyalty);
      }
      if (currentUrl.includes(NavType.Service.toLowerCase())) {
        setSelectedOption(NavType.Service);
      }
      if (currentUrl.includes(NavType.Recent.toLowerCase())) {
        setSelectedOption(NavType.Recent);
      }
      if (currentUrl.includes(NavType.Favorites.toLowerCase())) {
        setSelectedOption(NavType.Favorites);
      }
      if (currentUrl.includes(NavType.YourProfile.toLowerCase())) {
        setSelectedOption(NavType.YourProfile);
      }
      if (currentUrl.includes(NavType.Support.toLowerCase())) {
        setSelectedOption(NavType.Support);
      }
      if (currentUrl.includes(NavType.GetMobileApp.toLowerCase())) {
        setSelectedOption(NavType.GetMobileApp);
      }
      if (currentUrl.includes('privateincentivelookup')) {
        setSelectedOption(NavType.PrivateIncentiveLookup);
      }
    }

    setSelectedNavOption();
  }, [hideSideNav]);

  const isOptIn = dealerInfos.map((e) => isDealerOptIn(e.dealerId));
  const permissions = currentUser.permissions;
  const servicePortfolioIcon =
    isServicePortfolio && hasServicePortfolioEnabled ? 'service-conquest' : 'conquest';

  const onNavClick = (link: string, e: any = null): void => {
    setSelectedOption(link);
    redirectToUrl(link);
    dispatch(setIsCustomerProfileModalOpen(false));
    hideSideNav();
  };

  const isSelected = (link: string): boolean => {
    if (selectedOption === null) {
      const isLinkActive = isActiveTab(link);
      if (isLinkActive) {
        setSelectedOption(link);
      }
    }

    return link === selectedOption;
  };

  const handleOnStoreNameClick = (): void => {
    setSelectStoreModalIsOpen(true);
    hideSideNav();
  };

  const logout = (): void => {
    localStorage.removeItem('timerStart');
    logoutRedirect();
  };

  const getNavOption = (iconType: string, navType: string, color: string): JSX.Element => {
    return (
      <>
        <IconWrapper>
          <Icon size={theme.typography.h1} type={iconType} color={color} />
        </IconWrapper>
        <MenuItem color={color} marginLeft={-6}>
          {navType}
        </MenuItem>
      </>
    );
  };

  useEffect(() => {
    CommonService.getStores().then((res) => {
      if (res.length === 1) {
        setShowChangeButton(false);
      }
      res.filter((store: StoreDetails) => {
        if (store.storeCode === currentUser?.storeCode) {
          setStoreName(store.storeName);
        }
      });
    });
  }, [currentUser?.storeCode]);

  const hasPrivateIncentive = (): boolean => {
    if (privateIncentiveCount && privateIncentiveCount.count.length > 0) {
      return true;
    }
    return false;
  };

  return (
    <>
      <Overlay isLeftMobileMenu={leftMobileMenu} data-testid='overlay'></Overlay>
      <SideNavDiv
        data-testid='side-nav'
        ref={wrapperRef}
        width={`${leftMobileMenu ? '85vw' : '0'}`}
      >
        <HeaderArea data-testid='header-area'>
          <IconWrapper onClick={() => hideSideNav()}>
            <Icon
              size={theme.typography.h1}
              type='x'
              color={theme.palette.vibrantPurple}
              data-testid='close-icon'
            />
          </IconWrapper>
          <Header data-testid='header'>
            <HeaderImage src={BackgroundImage}></HeaderImage>
          </Header>
        </HeaderArea>
        <MainMenu paddingLeft='9'>
          <IF condition={Boolean(currentUser.isPrimaryUser) && Boolean(!currentUser.isShadowLogin)}>
            <MainMenuItemList>
              <NavLink onClick={handleOnStoreNameClick} data-testid='store-name' display='block'>
                <StoreName>
                  <IconWrapper>
                    <Icon
                      size={theme.typography.h1}
                      type='new-car'
                      color={theme.palette.white}
                      marginTop={7}
                    />
                  </IconWrapper>
                  <MenuItem color={theme.palette.white} marginLeft={-6} whiteSpace='nowrap'>
                    Store: {currentUser.storeCode}
                  </MenuItem>
                </StoreName>
                <MenuItem
                  color={theme.palette.white}
                  marginLeft={53}
                  display='flex'
                  {...(!isLandscape ? { width: '65vw' } : { whiteSpace: 'nowrap' })}
                >
                  {storeName}
                </MenuItem>
              </NavLink>
            </MainMenuItemList>
          </IF>
          <IF
            condition={
              !currentUser.isPrimaryUser && currentUser.isMultiStoreUser && showChangeButton
            }
          >
            <MainMenuItemList>
              <NavLink alignItem='start' onClick={handleOnStoreNameClick} data-testid='store-name'>
                <IconWrapper data-testid='icon-container' height='auto' alignItem='start'>
                  <Icon size={theme.typography.h1} type='new-car' color={theme.palette.white} />
                </IconWrapper>

                <MenuItem
                  color={theme.palette.white}
                  marginLeft={-6}
                  display='flex'
                  flexShrink={0}
                  wordBreak='break-word'
                  {...(!isLandscape ? { width: '47vw' } : {})}
                >
                  {storeName}
                </MenuItem>
                <ChangeButton data-testid='change-button'>CHANGE</ChangeButton>
              </NavLink>
            </MainMenuItemList>
          </IF>

          {!isIncentiveAdvantageType && (
            <MainMenuItemList isSelected={isSelected(NavType.Loyalty)}>
              <NavLink
                onClick={(event) => onNavClick(NavType.Loyalty, event)}
                data-testid={NavType.Loyalty}
              >
                {getNavOption('portfolio', NavType.Loyalty, theme.palette.white)}
              </NavLink>
            </MainMenuItemList>
          )}
          {!currentUser.isPrimaryUser && (
            <div className='row'>
              <MainMenuColumnWrapper isSelected={isSelected(NavType.Recent)}>
                <NavLink
                  onClick={() => {
                    onNavClick(NavType.Recent);
                  }}
                  data-testid={NavType.Recent}
                >
                  {getNavOption('clock-outline', NavType.Recent, theme.palette.white)}
                </NavLink>
              </MainMenuColumnWrapper>

              <MainMenuColumnWrapper isSelected={isSelected(NavType.Favorites)}>
                <NavLink
                  onClick={() => onNavClick(NavType.Favorites)}
                  data-testid={NavType.Favorites}
                >
                  {getNavOption('favorite', NavType.Favorites, theme.palette.white)}
                </NavLink>
              </MainMenuColumnWrapper>
            </div>
          )}
          {(isOptIn.includes(true) || hasPrivateIncentive()) && isPrivateIncentiveLookup && (
            <MainMenuItemList isSelected={isSelected(NavType.PrivateIncentiveLookup)}>
              <NavLink
                onClick={(event) => onNavClick(NavType.PrivateIncentiveLookup, event)}
                data-testid={NavType.PrivateIncentiveLookup}
                whiteSpace='nowrap'
              >
                {getNavOption(
                  'private-incentive',
                  NavType.PrivateIncentiveLookup,
                  theme.palette.white,
                )}
              </NavLink>
            </MainMenuItemList>
          )}
          {permissions.includes('updateConquest') &&
            !isIncentiveAdvantageType &&
            !isEssentialType &&
            !(isServicePortfolio && !hasServicePortfolioEnabled) && (
              <MainMenuItemList isSelected={isSelected(servicePortfolioNav)}>
                <NavLink
                  onClick={(event) => {
                    onNavClick(servicePortfolioNav, event);
                  }}
                  data-testid={servicePortfolioNav}
                >
                  {getNavOption(servicePortfolioIcon, servicePortfolioNav, theme.palette.white)}
                </NavLink>
              </MainMenuItemList>
            )}
        </MainMenu>
        <BottomMenu>
          <MainMenu>
            <MainMenuItemList paddingTop={10}>
              <NavLink
                onClick={() => onNavClick(NavType.YourProfile)}
                data-testid={NavType.YourProfile}
              >
                {getNavOption('profile', 'Your Profile', theme.palette.text.purple)}
              </NavLink>
            </MainMenuItemList>

            {mobileEnabled && !isPwa && (
              <MainMenuItemList>
                <NavLink
                  onClick={() => onNavClick(NavType.GetMobileApp)}
                  data-testid={NavType.GetMobileApp}
                >
                  {getNavOption(
                    'number-mobile',
                    'Get Mastermind Mobile',
                    theme.palette.text.purple,
                  )}
                </NavLink>
              </MainMenuItemList>
            )}
            <MainMenuItemList>
              <NavLink onClick={() => onNavClick(NavType.Support)} data-testid={NavType.Support}>
                {getNavOption('help-circle-full', NavType.Support, theme.palette.text.purple)}
              </NavLink>
            </MainMenuItemList>

            <MainMenuItemList>
              <NavLink onClick={logout} data-testid={NavType.LogOut}>
                {getNavOption('logout', 'Log Out', theme.palette.text.purple)}
              </NavLink>
            </MainMenuItemList>
          </MainMenu>
          <Footer>
            <NavLink
              href='https://www.automotivemastermind.com/terms-of-service/'
              target='_blank'
              data-testid='terms-of-service'
            >
              <MenuItem color={theme.palette.text.purple} marginLeft={-6}>
                Terms of Service
              </MenuItem>
            </NavLink>
            &nbsp;| &nbsp;&nbsp;
            <NavLink
              href='https://www.automotivemastermind.com/privacy-policy/'
              target='_blank'
              data-testid='privacy-policy'
            >
              <MenuItem color={theme.palette.text.purple} marginLeft={-6}>
                Privacy Policy
              </MenuItem>
            </NavLink>
          </Footer>
          <NavBottomText data-testid='menu-footer'>
            <MenuItem
              fontWeight={theme.typography.fontWeights.semibold}
              color={theme.palette.text.purple}
              display={'block'}
            >
              &copy; {currentYear()} automotiveMastermind&reg;
            </MenuItem>
            <MenuItem
              fontWeight={theme.typography.fontWeights.semibold}
              color={theme.palette.text.purple}
              display={'block'}
            >
              All rights reserved.
            </MenuItem>
          </NavBottomText>
        </BottomMenu>
        <SelectStoreModal
          modalIsOpen={selectStoreModalIsOpen}
          setIsOpen={setSelectStoreModalIsOpen}
        />
      </SideNavDiv>
    </>
  );
};
export default MobileSideNav;

type OverlayProps = {
  isLeftMobileMenu: boolean;
};
export const Overlay = styled.div<OverlayProps>`
  position: fixed;
  display: ${(props) => (props.isLeftMobileMenu ? 'block' : 'none')};
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 200;
`;
